'use client';

import { IStoreLists } from '@/interfaces/store';
import React, { useCallback } from 'react';
import { BasicRadio } from './BasicRadio';
import { Tooltip } from '@mantine/core';
import { IconGoogleMap } from '@/icons/IconGoogleMap';
import { ILocation } from '@/interfaces/location';
import { IWarehouse } from '@/interfaces/orders';
import { useScrollToView } from '@/hooks/useScrollToView';
import useHeaderStore from '@/store/header';

const EMPTY_STORE_LIST: IStoreLists[] = [];

function StoreDetails({
  showLocationModal,
  store,
  isEditable = false,
  userCurrentLatLong,
  setSelectedStore,
  selectedStore,
  setShowLocationModal,
  setMyLocation,
  isBOPIS,
  thanaData,
  setThanaId,
  thanaId,
  onChange,
  isWareHouse,
  wareHouse,
  closeOnSelect,
  isLoading = false,
}: Readonly<{
  showLocationModal?: boolean;
  store?: IStoreLists[];
  isEditable?: boolean;
  userCurrentLatLong?: { latitude?: number; longitude?: number };
  setSelectedStore?: Function;
  selectedStore?: IStoreLists;
  setShowLocationModal?: Function;
  setMyLocation?: Function;
  isBOPIS?: boolean;
  thanaData?: ILocation[];
  setThanaId?: Function;
  thanaId?: string;
  onChange?: Function;
  isWareHouse?: boolean;
  wareHouse?: IWarehouse;
  closeOnSelect?: boolean;
  isLoading?: boolean;
}>) {
  const setIsExpressEnabled = useHeaderStore((state) => state.setIsExpressEnabled);

  const handleSelectStore = useCallback(
    (storeItem: IStoreLists) => {
      if (selectedStore?.Id === storeItem.Id) {
        setSelectedStore?.({});
      } else {
        setSelectedStore?.(storeItem);
        if (closeOnSelect !== false) {
          setShowLocationModal?.(false);
        }
      }
    },
    [selectedStore, setSelectedStore, setShowLocationModal],
  );

  const storeScrollRef = useScrollToView(Boolean(showLocationModal), selectedStore, store ?? EMPTY_STORE_LIST);

  const hasNoStores = isBOPIS && !isLoading && (!store || store.length === 0);

  const renderDeliveryLocation = () => {
    if (isWareHouse) {
      return (
        <div className="mb-3 border border-solid border-[#C8C8C8] px-4 py-3">
          <div>
            <div className="flex justify-between">
              <BasicRadio
                className="cursor-pointer !text-xl font-semibold leading-[24px]"
                name="warehouse"
                value={wareHouse?.Name as string}
                label={wareHouse?.Name as string}
                isChecked={true}
                onChange={() => {}}
                selectedValues={[wareHouse?.Name as string]}
              />
            </div>
            <p className="mb-1 ml-8 text-base font-normal leading-[19.25px] text-black">{wareHouse?.Address}</p>
            <p className="mb-1 ml-8 text-base font-normal leading-[19.25px] text-black">{wareHouse?.ContactNumber}</p>
          </div>
        </div>
      );
    }

    if (isBOPIS) {
      return store?.map((storeItem, index) => (
        <div
          key={index}
          ref={selectedStore?.Id === storeItem?.Id ? storeScrollRef : undefined}
          className="border-b border-solid border-[#C8C8C8] px-4 py-3 last:border-b-0"
        >
          <div className="cursor-pointer" onClick={() => handleSelectStore(storeItem)}>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                <div onClick={(e) => e.stopPropagation()}>
                  <BasicRadio
                    className="cursor-pointer !text-xl font-semibold leading-[24px]"
                    name="nearByStore"
                    label={storeItem.Name}
                    value={storeItem.Name}
                    isChecked={selectedStore?.Id === storeItem?.Id}
                    onChange={() => handleSelectStore(storeItem)}
                    selectedValues={[storeItem.Name]}
                    onClick={(e) => {
                      const clickedStoreName = (e.target as HTMLInputElement).value;
                      if (selectedStore?.Name === clickedStoreName) {
                        setSelectedStore?.({});
                      }
                    }}
                  />
                </div>
              </div>
              {isEditable && (
                <p
                  className="cursor-pointer text-lg font-medium underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange?.();
                  }}
                >
                  Change
                </p>
              )}
            </div>
            <p className="mb-1 ml-8 text-base font-normal leading-[19.25px] text-black">{storeItem.Address}</p>
          </div>

          <div className="ml-8 flex items-center justify-between">
            <p className="text-base font-normal leading-[19.25px] text-black">{storeItem.PhoneNumber}</p>

            {userCurrentLatLong?.latitude && userCurrentLatLong?.longitude && (
              <Tooltip
                label={
                  <div className="min-w-[50px] whitespace-normal text-center leading-relaxed text-white">
                    <b>Get Direction</b>
                  </div>
                }
                color="dark"
                withArrow
                position="bottom-end"
              >
                <a
                  href={`https://www.google.com/maps/dir/${userCurrentLatLong?.latitude},${userCurrentLatLong?.longitude}/${storeItem.Latitude},${storeItem.Longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconGoogleMap className="icon w-6 p-0.5" />
                </a>
              </Tooltip>
            )}
          </div>
        </div>
      ));
    }

    // Default: Thana data
    return thanaData?.map((thana, index) => (
      <div key={index} className="mb-3 border border-solid border-[#C8C8C8] px-4 py-3">
        <div>
          <div className="flex justify-between">
            <BasicRadio
              className="cursor-pointer !text-xl font-semibold leading-[24px]"
              name="nearByStore"
              value={thana.Name}
              label={thana.Name}
              isChecked={thanaId === thana?.Id}
              onClick={(e) => {
                setThanaId?.(thana.Id);
                setMyLocation?.({
                  value: thana.Id,
                  label: thana.Name,
                });
                setIsExpressEnabled(true);
              }}
              onChange={() => {}}
              selectedValues={[thana.Name]}
            />
          </div>
        </div>
      </div>
    ));
  };

  return (
    <>
      {hasNoStores && (
        <div className="mb-3 border border-solid border-black px-4 py-3 text-center">
          <p className="text-lg font-normal leading-[19.25px] text-gray-500">No store found</p>
        </div>
      )}

      {renderDeliveryLocation()}
    </>
  );
}

export default StoreDetails;
